// Copyright (C) 2026 FlexiProof. All rights reserved.
//
// This source code file is a part of free software licensed under the terms of
// the MIT License as published by the Massachusetts Institute of Technology:
// you can use, copy, modify and distribute any part of it without limitation,
// subject to the conditions contained within that license.
//
// This source code file, and the software it forms a part of, IS PROVIDED "AS
// IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED. See the MIT License
// for more details.
//
// You should have received a copy of the MIT License along with this source
// code file in the root of this repository. If not, see one of the following
// 3rd party sites for a copy of the license template:
// - <https://opensource.org/licenses/MIT>
// - <https://choosealicense.com/licenses/mit>
// - <https://spdx.org/licenses/MIT>

/**
 * @file Basic container web component for stepped interfaces.
 * @author James Reid
 */

// @ts-check

// @@imports-utils
import { cloneTemplate, renderLucideIcons } from "../../utils/index.js"

// @@imports-types
import { steppedContainerCb } from "../../types/callbacks.js"
import {
    crossFadeElements,
    fadeElementIn,
    fadeElementOut
} from "../../utils/document/animations.js"
import { Layout } from "../../layouts/Layout.js"
import { STEP_TYPES } from "../../types/StepType.js"

// @@body
class SteppedContainer extends HTMLElement {
    #callback
    #steps = /** @type {Layout[]} */ ([])
    #stepIndex = NaN
    /** @type {?Layout} */ #startScreen = null
    /** @type {?Layout} */ #endScreen = null

    /**
     * @param {Layout[]} steps
     * @param {steppedContainerCb} callback
     */
    constructor(steps, callback) {
        super()

        for (const step of steps) {
            switch (step.type) {
                case STEP_TYPES.start: this.#startScreen = step; break
                case STEP_TYPES.end: this.#endScreen = step; break
                default: this.#steps.push(step)
            }
        }
        this.#callback = callback
    }

    connectedCallback() {
        if (!this.dataset.loading?.match(/^true$|^false$/)) {
            this.dataset.loading = true.toString()
        }

        this.append(
            this.#getProgressContainer(),
            this.#getLoadingContainer(),
            SteppedContainer.createContainer("content"),
            this.#getNavContainer(),
            SteppedContainer.createContainer("popover"),
            SteppedContainer.createContainer("modal")
        )

        renderLucideIcons()

        this.#callback(this)
    }

    start() {
        if (this.#startScreen) {
            this.#stepIndex = NaN
            this.#startScreen.render()
        }
        else { this.renderStep(this.#steps[0]) }
    }

    async end() {
        await Promise.all([
            fadeElementOut(this.progressContainer),
            fadeElementOut(this.navContainer)
        ])

        if (this.#endScreen) {
            this.#stepIndex = NaN
            this.#endScreen.render()
        }
        else { this.unload() }
    }

    /**
     * 
     * @param {Layout} step 
     */
    async renderStep(step) {
        // this.unload()
        this.#stepIndex = this.#steps.indexOf(step)

        const progress = this.progressContainer
        const nav = this.navContainer

        const progressFraction = this.#stepIndex / (this.#steps.length - 1)
        const progressLineForeground = /** @type {HTMLDivElement} */ (progress
            .querySelector(".progress-bar .foreground"))
        progressLineForeground.style.width = `${progressFraction * 100}%`

        const progressIndicator = /** @type {HTMLDivElement} */ (progress
            .querySelector(".progress-indicator"))
        for (let i = 1; i < progressIndicator.children.length; i++) {
            const step = progressIndicator.children[i]

            this.#stepIndex === i - 1
                ? step.classList.add("current")
                : step.classList.remove("current")

            this.#stepIndex > i - 1
                ? step.classList.add("complete")
                : step.classList.remove("complete")
        }

        progress.querySelector("div.step-details")?.classList.remove("closed")
        const header = progress.querySelector("div.step-details > h2")
        header?.addEventListener("animationend", () => {
            const newHeader = document.createElement("h2")
            newHeader.innerText = this.#steps[this.#stepIndex].title
            header.replaceWith(newHeader)
        })
        progress.querySelector("div.step-details > h2")?.classList.add("inactive")

        const stepProgressContent = /** @type {HTMLDivElement} */ (progress
            .querySelector("div.step-progress-content"))
        if (stepProgressContent.children.length) {
            await fadeElementOut(stepProgressContent)
            stepProgressContent.replaceChildren()
        }
        else { stepProgressContent.classList.add("closed") }

        // const stepInfo = /** @type {HTMLButtonElement} */ (progress
        //     .querySelector("div.step-details div.step-info"))
        // const stepInfoButton = /** @type {HTMLButtonElement} */ (progress
        //     .querySelector("div.step-details button"))
        // const closeStepInfoButton = /** @type {HTMLButtonElement} */ (progress
        //     .querySelector("div.step-details button + button"))
        // const priceDiv = /** @type {HTMLDivElement} */ (progress
        //     .querySelector("div.step-details div.step-title div"))

        // await fadeElementOut(stepInfo)
        // await fadeElementOut(closeStepInfoButton)
        // await fadeElementOut(priceDiv)
        // await fadeElementIn(stepInfoButton)

        // const newStepInfo = cloneTemplate(`step-info-${this.#steps[this.#stepIndex].id}`)
        // stepInfo.replaceChildren(newStepInfo)

        if (this.#stepIndex <= 0) {
            const previousButton = nav.getElementsByClassName("secondary")[0]
            previousButton.classList.add("hidden")

            const nextButton = nav.getElementsByClassName("primary")[0]
            nextButton.classList.remove("hidden")
        }
        else if (this.#stepIndex > 0 && this.#stepIndex < this.#steps.length - 1) {
            const navButtons = nav.getElementsByTagName("button")
            for (const navButton of navButtons) {
                navButton.classList.remove("hidden")
                navButton.classList.remove("inactive")
            }
        }
        else {
            const previousButton = nav.getElementsByClassName("secondary")[0]
            previousButton.classList.remove("hidden")

            const nextButton = nav.getElementsByClassName("primary")[0]
            nextButton.classList.add("hidden")
        }

        progress.classList.remove("closed")
        // 
        await this.unload()

        step.render()
    }

    setStep() {}

    incrementStep() {
        if (isNaN(this.#stepIndex)) { this.renderStep(this.#steps[0]) }
        else if (this.#stepIndex < this.#steps.length - 1) {
            // const currentStep = this.#steps[this.#stepIndex]
            // currentStep.unmount()
            this.renderStep(this.#steps[++this.#stepIndex])
        }
    }

    decrementStep() {
        if (this.#stepIndex > 0) {
            // const currentStep = this.#steps[this.#stepIndex]
            // currentStep.unmount()
            this.renderStep(this.#steps[--this.#stepIndex])
        }
        // else if () {} 
    }

    rerenderStep() {
        if (isNaN(this.#stepIndex)) { this.renderStep(this.#steps[0]) }
        else { this.renderStep(this.#steps[this.#stepIndex]) }
    }

    /**
     *
     * @param {HTMLElement} content
     */
    async load(content, { unloadModals = true, unloadPopovers = true } = {}) {
        // 
        if (unloadModals) { await this.unloadModal() }
        if (unloadPopovers) { await this.unloadPopover() }

        // 
        await fadeElementOut(this.loadingContainer)
        await fadeElementOut(this.contentContainer)

        this.contentContainer.replaceWith(content)
        renderLucideIcons()

        await fadeElementIn(this.contentContainer)

        this.loading = false
    }

    async unload({
        unloadModals = true,
        unloadPopovers = true,
        unloadNavs = false
    } = {}) {
        // 
        if (this.loading) { return }

        // 
        if (unloadModals) { await this.unloadModal() }
        if (unloadPopovers) { await this.unloadPopover() }

        const promises = []
        if (unloadNavs) {
            promises.push(
                fadeElementOut(this.progressContainer),
                fadeElementOut(this.navContainer)
            )
        }

        // 
        promises.push(fadeElementOut(this.contentContainer))
        await Promise.all(promises)
        await fadeElementIn(this.loadingContainer)

        // 
        this.loading = true
    }

    /**
     *
     * @param {HTMLElement} popover
     */
    async loadPopover(popover) {
        // await this.unloadPopover()

        this.popoverContainer.replaceWith(popover)
        renderLucideIcons()

        crossFadeElements(this.navContainer, this.popoverContainer)

        // fadeElementOut(this.navContainer)
        // fadeElementIn(this.popoverContainer)

        this.loading = false
    }

    /**
     * Unload existing popover container by first fading out any existing
     * popover content, then fading in the default nav container.
     */
    async unloadPopover({ replaceNav = true } = {}) {
        await fadeElementOut(this.popoverContainer)
        if (replaceNav) { await fadeElementIn(this.navContainer) }
    }

    async loadModal(modal) {
        this.modalContainer.replaceWith(modal)
        renderLucideIcons()

        for (const container of this.children) {
            if (container === this.modalContainer) { continue }
            container.classList.add("inactive", "blurred")
        }

        await fadeElementIn(this.modalContainer)
    }

    async unloadModal() {
        await fadeElementOut(this.modalContainer)
        for (const container of this.children) {
            container.classList.remove("inactive", "blurred")
        }
    }

    /**
     * 
     * @param {*} type 
     * @returns {HTMLElement} 
     */
    getContainer(type) {
        return document.getElementById(`${type}-container`)
    }

    #getLoadingContainer() {
        const loadingContainer = document.createElement("div")
        loadingContainer.classList.add("loading-container")
        const loadingBar = document.createElement("div")
        loadingBar.classList.add("loading-bar")
        const loadingBounce = document.createElement("div")
        loadingBounce.classList.add("loading-bounce", "--no-auto-animate")
        loadingBar.appendChild(loadingBounce)
        loadingContainer.append(loadingBar)

        loadingContainer.addEventListener("animationend", () => {
            this.loading = !loadingContainer.classList.contains("closed")

            const event = new Event("unload")
            this.dispatchEvent(event)
        })

        return loadingContainer
    }

    #getProgressContainer() {
        const progress = cloneTemplate("progress")

        for (const [index, step] of this.#steps.entries()) {
            // Ignore step - remove when step are only steps 
            if (step.type !== STEP_TYPES.step) { continue }

            const progressStep = cloneTemplate("progress-step")

            progressStep.addEventListener("click", () => {
                if (index < this.#stepIndex) { this.renderStep(step) }
            })

            progressStep.dataset["title"] = step.title;
            /** @type {HTMLSpanElement} */ (progressStep
                .querySelector("span.step-number"))
                .innerText = `${index + 1}`;

            /** @type {HTMLDivElement} */ (progress
                .querySelector("div.progress-indicator"))
                .appendChild(progressStep)

            const stepName = cloneTemplate("step-name");
            /** @type {HTMLSpanElement} */ (stepName
                .querySelector("span.step-name"))
                .innerText = step.name;

            /** @type {HTMLDivElement} */ (progress
                .querySelector("div.step-names"))
                .appendChild(stepName)
        }

        const stepInfo = /** @type {HTMLButtonElement} */ (progress
            .querySelector("div.step-details div.step-info"))

        // const stepInfoButton = /** @type {HTMLButtonElement} */ (progress
        //     .querySelector("div.step-details button"))
        // stepInfoButton.addEventListener("click", () => {
        //     fadeElementOut(stepInfoButton)
        //         .then(() => {
        //             fadeElementIn(stepInfo)
        //             fadeElementIn(closeStepInfoButton)
        //         })
        // })

        // const closeStepInfoButton = /** @type {HTMLButtonElement} */ (progress
        //     .querySelector("div.step-details button + button"))
        // closeStepInfoButton.addEventListener("click", () => {
        //     fadeElementOut(stepInfo)
        //     fadeElementOut(closeStepInfoButton)
        //         .then(() => fadeElementIn(stepInfoButton))
        // })

        return progress
    }

    #getNavContainer() {
        const nav = cloneTemplate("nav")

        nav.querySelector("button.secondary")
            ?.addEventListener("click", () => { this.decrementStep() })
        nav.querySelector("button.primary")
            ?.addEventListener("click", () => { this.incrementStep() })

        return nav
    }

    get loading() { return this.dataset.loading === "false" ? false : true }

    set loading(isLoading) { this.dataset.loading = isLoading.toString() }

    get loadingContainer() {
        return /** @type {HTMLDivElement} */ (this
            .getElementsByClassName("loading-container")[0])
    }

    get progressContainer() {
        return /** @type {HTMLDivElement} */ (this
            .querySelector("nav.top"))
    }

    get contentContainer() {
        return /** @type {HTMLDivElement} */ (this
            .getElementsByClassName("content-container")[0])
    }

    get popoverContainer() {
        return /** @type {HTMLDivElement} */ (this
            .getElementsByClassName("popover-container")[0])
    }

    get modalContainer() {
        return /** @type {HTMLDivElement} */ (this
            .getElementsByClassName("modal-container")[0])
    }

    get navContainer() {
        return /** @type {HTMLElement} */ (this
            .querySelector("nav.bottom"))
    }

    /**
     * Generate content wrapper container which may be used to render content
     * inside stepped container.
     * @returns {HTMLDivElement} Content wrapper container.
     */
    static contentContainer() {
        const container = document.createElement("div")
        container.classList.add("content-container", "closed")

        return container
    }

    /**
     * Generate content wrapper container which may be used to render a bottom
     * popover inside stepped container.
     * @returns {HTMLDivElement} Popover wrapper container.
     */
    static popoverContainer() {
        const container = document.createElement("div")
        container.classList.add("popover-container", "closed")

        return container
    }

    /**
     * Generate content wrapper container which may be used to render a modal
     * inside stepped container on top of existing content.
     * @returns {HTMLDivElement} Modal wrapper container.
     */
    static modalContainer() {
        const container = document.createElement("div")
        container.classList.add("modal-container", "closed")

        return container
    }

    /**
     * Generate wrapper container which may be used to render content of a given
     * type inside a tabbed container instance.
     * @param { } type Container type which will be used in class
     * name of returned element.
     * @returns {HTMLDivElement} Wrapper container of requested type.
     */
    static createContainer(type) {
        const container = document.createElement("div")
        container.classList.add(`${type}-container`, "closed")

        return container
    }


    static get customName() {
        return "stepped-container"
    }
}

// @@exports
export { SteppedContainer }
